import { useState } from 'react';
import { Paper, Stack, Text, Divider, Grid, Badge, Button, Group, Alert, Loader, Progress, Card } from '@mantine/core';
import { IconCreditCard, IconAlertCircle, IconCheck, IconCalendar, IconBox, IconUsers, IconForms, IconReportAnalytics, IconHeadset } from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useSubscription, useCreatePortalSession, useCreateStripeCustomer } from '@/hooks/api/useSubscription';
import { notifications } from '@mantine/notifications';

export function SubscriptionSettings() {
  const { organizationId } = useOrganization();
  const { data: subscription, isLoading, refetch } = useSubscription(organizationId);
  const createPortalSession = useCreatePortalSession();
  const createStripeCustomer = useCreateStripeCustomer();
  const [isSettingUp, setIsSettingUp] = useState(false);

  // Access control is handled by the parent SettingsPage via canAccess('billing')

  if (isLoading) {
    return (
      <Paper withBorder p="md">
        <Group justify="center" p="xl">
          <Loader size="md" />
          <Text>Loading subscription information...</Text>
        </Group>
      </Paper>
    );
  }

  if (!subscription) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="No Subscription Data" color="yellow">
        Unable to load subscription information. Please contact support if this issue persists.
      </Alert>
    );
  }

  const handleManageBilling = async () => {
    if (!organizationId) return;

    setIsSettingUp(true);
    try {
      // If no Stripe customer exists, create one first
      if (!subscription?.stripe_customer_id) {
        await createStripeCustomer.mutateAsync(organizationId);
        // Refetch subscription to get the new customer ID
        await refetch();
      }

      await createPortalSession.mutateAsync(organizationId);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to open billing portal. Please try again.',
        color: 'red',
      });
    } finally {
      setIsSettingUp(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'trialing':
        return 'blue';
      case 'past_due':
        return 'yellow';
      case 'canceled':
      case 'incomplete':
      case 'incomplete_expired':
      case 'unpaid':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: string | null) => {
    if (!status) return 'Unknown';
    return status.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <Stack gap="lg">
      <Paper withBorder p="md">
        <Stack gap="md">
          <div>
            <Text fw={600} size="lg" mb="xs">
              Subscription Overview
            </Text>
            <Text c="dimmed" size="sm">
              Manage your organization's subscription and billing
            </Text>
          </div>

          <Divider />

          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <div>
                <Text size="sm" fw={500} mb={4}>
                  Status
                </Text>
                <Badge
                  size="lg"
                  color={getStatusColor(subscription.subscription_status)}
                  variant="light"
                  leftSection={<IconCheck size={16} />}
                >
                  {getStatusLabel(subscription.subscription_status)}
                </Badge>
                {subscription.subscription_cancel_at_period_end && (
                  <Text size="xs" c="dimmed" mt={4}>
                    Cancels on {formatDate(subscription.subscription_period_end)}
                  </Text>
                )}
              </div>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <div>
                <Text size="sm" fw={500} mb={4}>
                  Current Plan
                </Text>
                <Text size="md" fw={600}>
                  {subscription.subscription_plan || 'No active plan'}
                </Text>
              </div>
            </Grid.Col>

            {subscription.subscription_period_start && subscription.subscription_period_end && (
              <>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <div>
                    <Text size="sm" fw={500} mb={4}>
                      Current Period Start
                    </Text>
                    <Group gap="xs">
                      <IconCalendar size={16} />
                      <Text size="sm">{formatDate(subscription.subscription_period_start)}</Text>
                    </Group>
                  </div>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 6 }}>
                  <div>
                    <Text size="sm" fw={500} mb={4}>
                      Current Period End
                    </Text>
                    <Group gap="xs">
                      <IconCalendar size={16} />
                      <Text size="sm">{formatDate(subscription.subscription_period_end)}</Text>
                    </Group>
                  </div>
                </Grid.Col>
              </>
            )}
          </Grid>

          {subscription.trial_mode && (
            <Alert icon={<IconAlertCircle size={16} />} title="Trial Mode" color="blue">
              Your organization is currently in trial mode.
              {subscription.trial_expires_at && (
                <Text size="sm" mt={4}>
                  Trial expires: {formatDate(subscription.trial_expires_at)}
                </Text>
              )}
            </Alert>
          )}

          <Divider />

          <div>
            <Text size="sm" fw={500} mb="md">
              Plan Limits
            </Text>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Card withBorder p="sm" radius="md">
                  <Group gap="xs" mb="xs">
                    <IconBox size={18} color="var(--mantine-color-blue-6)" />
                    <Text size="sm" fw={500}>Assets</Text>
                  </Group>
                  {subscription.asset_limit === null ? (
                    <Badge color="green" variant="light" size="lg">Unlimited</Badge>
                  ) : (
                    <>
                      <Text size="lg" fw={600}>
                        {subscription.current_asset_count} / {subscription.asset_limit}
                      </Text>
                      <Progress
                        value={(subscription.current_asset_count / subscription.asset_limit) * 100}
                        color={subscription.current_asset_count >= subscription.asset_limit ? 'red' : 'blue'}
                        size="sm"
                        mt="xs"
                      />
                    </>
                  )}
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Card withBorder p="sm" radius="md">
                  <Group gap="xs" mb="xs">
                    <IconUsers size={18} color="var(--mantine-color-blue-6)" />
                    <Text size="sm" fw={500}>Users</Text>
                  </Group>
                  {subscription.user_limit === null ? (
                    <Badge color="green" variant="light" size="lg">Unlimited</Badge>
                  ) : (
                    <Text size="lg" fw={600}>{subscription.user_limit}</Text>
                  )}
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Card withBorder p="sm" radius="md">
                  <Group gap="xs" mb="xs">
                    <IconForms size={18} color="var(--mantine-color-blue-6)" />
                    <Text size="sm" fw={500}>Custom Fields</Text>
                  </Group>
                  {subscription.custom_field_limit === null ? (
                    <Badge color="green" variant="light" size="lg">Unlimited</Badge>
                  ) : (
                    <Text size="lg" fw={600}>{subscription.custom_field_limit}</Text>
                  )}
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Card withBorder p="sm" radius="md">
                  <Group gap="xs" mb="xs">
                    <IconReportAnalytics size={18} color="var(--mantine-color-blue-6)" />
                    <Text size="sm" fw={500}>Scheduled Reports</Text>
                  </Group>
                  {subscription.scheduled_report_limit === null ? (
                    <Badge color="green" variant="light" size="lg">Unlimited</Badge>
                  ) : (
                    <Text size="lg" fw={600}>{subscription.scheduled_report_limit}</Text>
                  )}
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <Card withBorder p="sm" radius="md">
                  <Group gap="xs" mb="xs">
                    <IconHeadset size={18} color="var(--mantine-color-blue-6)" />
                    <Text size="sm" fw={500}>Priority Support</Text>
                  </Group>
                  {subscription.priority_support && subscription.priority_support > 0 ? (
                    <Badge color="green" variant="light" size="lg">Included</Badge>
                  ) : (
                    <Badge color="gray" variant="light" size="lg">Not Included</Badge>
                  )}
                </Card>
              </Grid.Col>
            </Grid>
          </div>

          <Divider />

          <div>
            <Text size="sm" fw={500} mb="md">
              Billing Management
            </Text>
            <Group>
              <Button
                leftSection={<IconCreditCard size={16} />}
                onClick={handleManageBilling}
                loading={isSettingUp || createPortalSession.isPending}
              >
                {subscription.stripe_customer_id ? 'Manage Billing & Payment Methods' : 'Set Up Billing'}
              </Button>
            </Group>
            <Text size="xs" c="dimmed" mt="xs">
              Update payment methods, view invoices, and manage your subscription through Stripe's secure billing portal.
            </Text>
          </div>
        </Stack>
      </Paper>

      {subscription.subscription_status === 'past_due' && (
        <Alert icon={<IconAlertCircle size={16} />} title="Payment Issue" color="red">
          There was a problem processing your last payment. Please update your payment method to avoid service interruption.
        </Alert>
      )}

      {subscription.subscription_status === 'canceled' && (
        <Alert icon={<IconAlertCircle size={16} />} title="Subscription Canceled" color="orange">
          Your subscription has been canceled. You may have limited access to features.
          {subscription.subscription_canceled_at && (
            <Text size="sm" mt={4}>
              Canceled on: {formatDate(subscription.subscription_canceled_at)}
            </Text>
          )}
        </Alert>
      )}
    </Stack>
  );
}
